// ====================================================================
// THE GREAT BUNNY ROUNDUP — a map-wide critter chase
// ====================================================================
// The game's third quest, and the first that sends Chocky to every corner of
// the map. The Bunny Wrangler stands in the north meadow with an empty hutch —
// it popped open and the whole warren hopped off across Chockytown: out toward
// Wordville, deep into the western Grove, down in the southern fields. Talk to
// the Wrangler (the same bump-zone speech bubble everything else uses) and a
// gold "look here!" arrow bobs over each loose bunny. Catching one is EXPLORING,
// not jumping: the per-frame update samples the player's position, and simply
// walking up to a bunny pops it home (a puff of dust, the arrow winks out, the
// tally ticks up). Round up all six and Chocky earns the Bunny Ears plus a
// little coin burst at the hutch.
//
// Persistence (game/save.js): only the terminal 'done' state round-trips
// (save.js whitelists quest values), so a finished roundup stays finished and
// the bunnies stay home across reloads. A roundup left half-done resets on
// reload — re-chasing a few bunnies is play, not loss (the same call the dig
// and fennec quests make about their mid-quest progress).
//
// Built through a ctx from main.js (the zone/store/quest pattern): world FX,
// bump zones, the coin reward, and the wallet stay main.js-owned; this module
// owns the Wrangler, the bunnies, their arrows, and the quest state machine.

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { LOW } from '../core/tier.js';
import { loadSave, patchSave } from './save.js';
import { equip, getEquipped } from './outfits.js';
import { heightAt } from '../world/heightfield.js';
import { spawnActor, despawnActor } from '../world/actors.js';
import { markDynamic } from '../world/freezeStatics.js';

const QUEST_ID = 'bunnies';
const WRANGLER = { x: 3, z: 10 };
const HUTCH = { x: 4.6, z: 11 };
const CATCH_RADIUS = 1.3;
const ANIM_RADIUS2 = 900;               // only hop/bob bunnies within 30 units

// The warren, scattered to the four winds (and both themed zones). Each gets a
// coat colour so the herd reads as individuals, not clones.
const BUNNIES = [
  { x: 8,   z: 11,  coat: 0xb89878 },   // north meadow, by the hutch — tan
  { x: 28,  z: 6,   coat: 0x9a9aa2 },   // east road toward Wordville — grey
  { x: 44,  z: -9,  coat: 0xf0ece6 },   // south of the Wordville plaza — white
  { x: -16, z: -6,  coat: 0x8a6e54 },   // the western meadow — brown
  { x: -39, z: 9,   coat: 0xc8b49a },   // out by the Grove oasis — sandy
  { x: 10,  z: -17, coat: 0x7c6a5a },   // the southern fields — dark brown
];

let state = null;                       // null | 'chasing' | 'done'
function setState(s) {
  state = s;
  patchSave({ quests: { ...loadSave().quests, [QUEST_ID]: s } });
}

// ---- mesh helpers (the wordville/quest trick: vertex-coloured primitives
// merged into ONE geometry, one cheap Lambert material) ---------------------
function tinted(geo, hex) {
  const c = new THREE.Color(hex);
  const n = geo.attributes.position.count;
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b; }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geo;
}
function coloredBox(w, h, d, x, y, z, hex) {
  const g = new THREE.BoxGeometry(w, h, d);
  g.translate(x, y, z);
  return tinted(g, hex);
}

// ---- The Bunny Wrangler: a box-built kid in dungarees with a carrot in hand.
// Two draw calls (body + head); the head turns to watch the player, like Pip.
function buildWrangler(ctx) {
  const DENIM = 0x4a6a9c, SHIRT = 0xe0b34a, SKIN = 0xe8c49a, HAIR = 0x6a4a32;
  const CARROT = 0xe8732c, LEAF = 0x4a8a3c;

  const carrot = tinted(new THREE.ConeGeometry(0.05, 0.22, 7), CARROT);
  carrot.rotateX(Math.PI);
  carrot.translate(0.34, 0.5, 0.08);
  const leaf = tinted(new THREE.ConeGeometry(0.04, 0.12, 5), LEAF);
  leaf.translate(0.34, 0.64, 0.08);

  const bodyGeo = mergeGeometries([
    coloredBox(0.15, 0.3, 0.16,   0.11, 0.15, 0,    DENIM),    // legs
    coloredBox(0.15, 0.3, 0.16,  -0.11, 0.15, 0,    DENIM),
    coloredBox(0.17, 0.09, 0.2,   0.11, 0.045, 0.02, 0x5a4632), // shoes
    coloredBox(0.17, 0.09, 0.2,  -0.11, 0.045, 0.02, 0x5a4632),
    coloredBox(0.44, 0.46, 0.28,  0,    0.6, 0,     SHIRT),    // torso
    coloredBox(0.46, 0.3, 0.3,    0,    0.46, 0.01, DENIM),    // dungaree bib
    coloredBox(0.13, 0.42, 0.13,  0.32, 0.68, 0,    SHIRT),    // arms
    coloredBox(0.13, 0.42, 0.13, -0.32, 0.68, 0,    SHIRT),
    coloredBox(0.11, 0.1, 0.11,   0.32, 0.44, 0,    SKIN),     // hands
    coloredBox(0.11, 0.1, 0.11,  -0.32, 0.44, 0,    SKIN),
    carrot, leaf,
  ], false);

  const headGeo = mergeGeometries([
    coloredBox(0.32, 0.32, 0.32,  0, 0.16, 0,     SKIN),       // head
    coloredBox(0.34, 0.12, 0.34,  0, 0.34, 0,     HAIR),       // hair cap
    coloredBox(0.06, 0.06, 0.06,  0, 0.13, 0.18,  0xdfae84),   // nose
  ], false);

  const mat = new THREE.MeshLambertMaterial({ vertexColors: true });
  const group = new THREE.Group();
  const body = new THREE.Mesh(bodyGeo, mat);
  body.castShadow = !LOW;
  group.add(body);
  const head = new THREE.Mesh(headGeo, mat);
  head.position.y = 1.0;
  group.add(head);

  group.position.set(WRANGLER.x, heightAt(WRANGLER.x, WRANGLER.z), WRANGLER.z);
  markDynamic(group);   // update() bobs and turns the wrangler
  ctx.propsGroup.add(group);
  return { group, head };
}

// ---- The empty hutch: a little wooden box on stilts with a dark open doorway,
// so the meadow reads "something hopped out of here." One static prop.
function buildHutch(ctx) {
  const WOOD = 0x9c6e44, WOOD_DK = 0x6e4a2c, DARK = 0x241a12, ROOF = 0xb7503c;
  const parts = [
    coloredBox(0.8, 0.18, 0.5,   0, 0.42, 0,    WOOD_DK),      // raised floor
    coloredBox(0.1, 0.42, 0.1,   0.32, 0.21, 0.18, WOOD_DK),   // legs
    coloredBox(0.1, 0.42, 0.1,  -0.32, 0.21, 0.18, WOOD_DK),
    coloredBox(0.1, 0.42, 0.1,   0.32, 0.21, -0.18, WOOD_DK),
    coloredBox(0.1, 0.42, 0.1,  -0.32, 0.21, -0.18, WOOD_DK),
    coloredBox(0.8, 0.46, 0.5,   0, 0.74, 0,    WOOD),         // box body
    coloredBox(0.34, 0.36, 0.06, 0, 0.7, 0.25,  DARK),         // open doorway
    coloredBox(0.92, 0.12, 0.6,  0, 1.02, 0,    ROOF),         // little roof
  ];
  const mesh = new THREE.Mesh(mergeGeometries(parts), new THREE.MeshLambertMaterial({ vertexColors: true }));
  mesh.castShadow = !LOW;
  mesh.position.set(HUTCH.x, heightAt(HUTCH.x, HUTCH.z), HUTCH.z);
  mesh.rotation.y = -0.5;
  ctx.propsGroup.add(mesh);
}

// ---- A bunny: one merged mesh (body, head, ears, tail, feet) with a gentle
// whole-group hop. Small and far, so it never needs an articulated head.
function buildBunny(ctx, x, z, coat) {
  const dk = new THREE.Color(coat).multiplyScalar(0.82).getHex();
  const WHITE = 0xfafafa, PINK = 0xf3a9c0, EYE = 0x2a2018;

  const parts = [
    coloredBox(0.3, 0.24, 0.34,  0,    0.16, 0,     coat),     // haunches/body
    coloredBox(0.2, 0.2, 0.2,    0,    0.34, 0.16,  coat),     // head, forward
    coloredBox(0.07, 0.05, 0.06, 0,    0.3,  0.27,  PINK),     // nose/muzzle
    coloredBox(0.04, 0.04, 0.03, 0.06, 0.38, 0.25,  EYE),      // eyes
    coloredBox(0.04, 0.04, 0.03,-0.06, 0.38, 0.25,  EYE),
    coloredBox(0.1, 0.07, 0.18,  0.1,  0.05, 0.16,  dk),       // front feet
    coloredBox(0.1, 0.07, 0.18, -0.1,  0.05, 0.16,  dk),
  ];
  // Tall ears, white-backed, tipped slightly back.
  for (const side of [-1, 1]) {
    const ear = tinted(new THREE.CapsuleGeometry(0.045, 0.22, 4, 7), WHITE);
    ear.rotateX(-0.25);
    ear.rotateZ(side * 0.18);
    ear.translate(side * 0.07, 0.5, 0.12);
    parts.push(ear);
  }
  // Fluffy tail.
  const tail = tinted(new THREE.SphereGeometry(0.08, 7, 6), WHITE);
  tail.translate(0, 0.22, -0.2);
  parts.push(tail);

  const mesh = new THREE.Mesh(mergeGeometries(parts), new THREE.MeshLambertMaterial({ vertexColors: true }));
  mesh.castShadow = !LOW;
  const group = new THREE.Group();
  group.add(mesh);
  group.position.set(x, heightAt(x, z), z);
  group.rotation.y = Math.random() * Math.PI * 2;
  markDynamic(group);   // loose bunnies hop
  ctx.propsGroup.add(group);
  return group;
}

export function initQuest(ctx) {
  state = loadSave().quests[QUEST_ID] || null;
  const done = state === 'done';

  const wrangler = buildWrangler(ctx);
  const wranglerBaseY = wrangler.group.position.y;
  const REST_YAW = Math.atan2(-WRANGLER.x, -WRANGLER.z);   // face back toward the village
  let yaw = REST_YAW;
  wrangler.group.rotation.y = yaw;
  wrangler.actor = spawnActor('bunny-wrangler', wrangler);
  buildHutch(ctx);

  // Each bunny gets a body and a gold "look here!" arrow that bobs overhead
  // while it's loose. A finished roundup starts with every bunny already home
  // (hidden) — the hutch is full again.
  const bunnies = BUNNIES.map((b) => {
    const group = buildBunny(ctx, b.x, b.z, b.coat);
    const arrow = new THREE.Mesh(
      new THREE.ConeGeometry(0.16, 0.34, 4),
      new THREE.MeshBasicMaterial({ color: 0xffd23f })
    );
    arrow.rotation.x = Math.PI;                            // point down at the bunny
    arrow.position.set(b.x, heightAt(b.x, b.z) + 1.1, b.z);
    arrow.visible = false;
    markDynamic(arrow);   // bobs/spins over a loose bunny
    ctx.propsGroup.add(arrow);
    const entry = { ...b, group, arrow, caught: done, phase: Math.random() * Math.PI * 2 };
    entry.actor = spawnActor('bunny', entry);
    if (done) { group.visible = false; }
    return entry;
  });
  let caught = done ? bunnies.length : 0;

  function showArrows(on) {
    for (const b of bunnies) b.arrow.visible = on && !b.caught;
  }

  // ---- Talking to the Wrangler: a sticky bubble while you stand with him.
  // No accept button — saying hello starts the roundup (kid-proof).
  ctx.addBumpZone(WRANGLER.x, WRANGLER.z, 1.7, 'bunny-wrangler', (sourceId) => {
    if (!state) {
      setState('chasing');
      showArrows(true);
      ctx.showBumpMsg('🐰 BUNNY WRANGLER', "Oh no, Chocky — my hutch popped open and ALL my bunnies hopped off across Chockytown! Chase 'em down — just walk up to each one and it'll hop home. Follow the gold arrows!", 0, sourceId);
    } else if (state === 'chasing') {
      const left = bunnies.length - caught;
      ctx.showBumpMsg('🐰 BUNNY WRANGLER', `Still ${left} ${left === 1 ? 'bunny' : 'bunnies'} on the loose! Follow the gold arrows — they're scattered all over the map.`, 0, sourceId);
    } else {
      ctx.showBumpMsg('🐰 BUNNY WRANGLER', "Every last bunny's back in the hutch — you're a true Chockytown hero! Those Bunny Ears suit you 🐰", 0, sourceId);
    }
  }, (sourceId) => ctx.hideBumpMsg(sourceId));

  function catchBunny(b) {
    b.caught = true;
    b.group.visible = false;
    b.arrow.visible = false;
    caught++;
    ctx.spawnDust(b.x, heightAt(b.x, b.z) + 0.1, b.z, 12, 1.2);
    ctx.addCamShake(0.12);
    ctx.triggerFace('grin', 1.0);
    if (caught >= bunnies.length) { completeRoundup(); return; }
    const left = bunnies.length - caught;
    ctx.showBumpMsg('🐰 GOTCHA!', `Bunny home safe! ${caught} of ${bunnies.length} rounded up — ${left} to go. Follow the gold arrows!`, 3, 'bunny');
  }

  function completeRoundup() {
    setState('done');
    showArrows(false);
    ctx.addCamShake(0.28);
    ctx.triggerFace('grin', 1.6);
    // The wardrobe reward: own the Bunny Ears, wear them if bare-headed (never
    // knock off a hat the kid paid for) — plus a little coin burst at the hutch.
    const owned = new Set(loadSave().owned);
    owned.add('bunnyears');
    patchSave({ owned: [...owned] });
    if (!getEquipped().head) equip('head', 'bunnyears');
    ctx.spawnQuestCoins(HUTCH.x, HUTCH.z);
    ctx.showBumpMsg('🐰 ALL HOME!', "You rounded up the whole warren! Your reward — a pair of BUNNY EARS — is waiting in Chocky's Closet.", 8, 'bunny');
  }

  // Per-frame (play mode): the Wrangler breathes and turns to watch the player;
  // loose bunnies near the player hop in place and their arrows spin; walking
  // within reach of one catches it. No allocation in here.
  function update(dt, t) {
    const p = ctx.getPlayerPos();

    const kdx = p.x - WRANGLER.x, kdz = p.z - WRANGLER.z;
    if (kdx * kdx + kdz * kdz < 2025) {                   // Wrangler asleep past ~45 units
      wrangler.group.position.y = wranglerBaseY + Math.sin(t * 2.2) * 0.015;
      const want = (kdx * kdx + kdz * kdz < 49) ? Math.atan2(kdx, kdz) : REST_YAW;
      let diff = want - yaw;
      while (diff > Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;
      yaw += diff * Math.min(1, dt * 3);
      wrangler.group.rotation.y = yaw;
    }

    if (state !== 'chasing') return;
    for (const b of bunnies) {
      if (b.caught) continue;
      const dx = p.x - b.x, dz = p.z - b.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < ANIM_RADIUS2) {                            // lively only when nearby
        const hop = Math.abs(Math.sin(t * 5 + b.phase));
        b.group.position.y = heightAt(b.x, b.z) + hop * 0.1;
        b.arrow.position.y = heightAt(b.x, b.z) + 1.05 + Math.sin(t * 3 + b.phase) * 0.12;
        b.arrow.rotation.y += dt * 1.5;
      }
      if (d2 < CATCH_RADIUS * CATCH_RADIUS) catchBunny(b);
    }
  }

  function dispose() {
    despawnActor(wrangler.actor);
    for (const b of bunnies) despawnActor(b.actor);
  }

  // Wayfinding beacon for the minimap compass (src/game/minimap.js): while the
  // roundup is on, point at the nearest loose bunny (they're scattered map-wide,
  // so "nearest" is the useful one to chase next); otherwise nothing.
  function getBeacon(px = 0, pz = 0) {
    if (state !== 'chasing') return null;
    let best = null, bestD = Infinity, left = 0;
    for (const b of bunnies) {
      if (b.caught) continue;
      left++;
      const d = (b.x - px) * (b.x - px) + (b.z - pz) * (b.z - pz);
      if (d < bestD) { bestD = d; best = b; }
    }
    return best ? { x: best.x, z: best.z, label: `Bunnies: ${left} left` } : null;
  }

  return { update, getBeacon, dispose };
}
